import React from "react";
import { NavLink, Routes, Route, Navigate } from "react-router-dom";
import { User, Briefcase, GraduationCap, Rocket, Code2, Award, PenSquare, MessageSquare, Mail, Upload, Sparkles, Trophy, Image, FolderOpen } from "lucide-react";
import ProfileEditor from "../components/admin/ProfileEditor";
import CollectionEditor from "../components/admin/CollectionEditor";
import BlogEditor from "../components/admin/BlogEditor";
import MessagesView from "../components/admin/MessagesView";
import ResumeUploader from "../components/admin/ResumeUploader";
import MediaLibrary from "../components/admin/MediaManager";

const TABS = [
  { to: "profile", label: "Profile", icon: User },
  { to: "media", label: "Media", icon: FolderOpen },
  { to: "experience", label: "Experience", icon: Briefcase },
  { to: "projects", label: "Projects", icon: Rocket },
  { to: "skills", label: "Skills", icon: Code2 },
  { to: "certifications", label: "Certifications", icon: Award },
  { to: "achievements", label: "Achievements", icon: Trophy },
  { to: "gallery", label: "Gallery", icon: Image },
  { to: "education", label: "Education", icon: GraduationCap },
  { to: "testimonials", label: "Testimonials", icon: MessageSquare },
  { to: "blog", label: "Blog", icon: PenSquare },
  { to: "messages", label: "Messages", icon: Mail },
  { to: "resume", label: "Resume", icon: Upload }
];

export default function AdminPage() {
  return (
    <div className="pt-24 min-h-screen" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center gap-3 mb-8">
          <Sparkles className="w-6 h-6 text-volt" />
          <h1 className="font-display font-black text-3xl tracking-tight">Control Room</h1>
        </div>
        <div className="grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3">
            <nav className="border border-white/10 bg-ink-900 divide-y divide-white/10">
              {TABS.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-5 py-3.5 text-sm uppercase tracking-wider transition ${
                      isActive ? "bg-volt text-ink-950 font-semibold" : "text-fog-200 hover:bg-white/5"
                    }`
                  }
                  data-testid={`admin-tab-${t.to}`}
                >
                  <t.icon className="w-4 h-4" />
                  {t.label}
                </NavLink>
              ))}
            </nav>
          </aside>
          <section className="lg:col-span-9 min-h-[400px]">
            <Routes>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<ProfileEditor />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="experience" element={<CollectionEditor title="Experience" endpoint="/experience" fields={EXPERIENCE_FIELDS} />} />
              <Route path="projects" element={<CollectionEditor title="Projects" endpoint="/projects" fields={PROJECT_FIELDS} />} />
              <Route path="skills" element={<CollectionEditor title="Skill Groups" endpoint="/skills" fields={SKILL_FIELDS} />} />
              <Route path="certifications" element={<CollectionEditor title="Certifications" endpoint="/certifications" fields={CERT_FIELDS} />} />
              <Route path="achievements" element={<CollectionEditor title="Achievements" endpoint="/achievements" fields={ACHIEVEMENT_FIELDS} />} />
              <Route path="gallery" element={<CollectionEditor title="Gallery" endpoint="/gallery" fields={GALLERY_FIELDS} />} />
              <Route path="education" element={<CollectionEditor title="Education" endpoint="/education" fields={EDU_FIELDS} />} />
              <Route path="testimonials" element={<CollectionEditor title="Testimonials" endpoint="/testimonials" fields={TESTI_FIELDS} />} />
              <Route path="blog" element={<BlogEditor />} />
              <Route path="messages" element={<MessagesView />} />
              <Route path="resume" element={<ResumeUploader />} />
            </Routes>
          </section>
        </div>
      </div>
    </div>
  );
}

const EXPERIENCE_FIELDS = [
  { name: "company", label: "Company", required: true },
  { name: "role", label: "Role", required: true },
  { name: "location", label: "Location" },
  { name: "start_date", label: "Start Date", required: true, placeholder: "Jun 2025" },
  { name: "end_date", label: "End Date", placeholder: "Jan 2025 or leave blank" },
  { name: "current", label: "Currently Here?", type: "bool" },
  { name: "description", label: "Description", type: "textarea" },
  { name: "bullets", label: "Bullets (one per line)", type: "list" },
  { name: "tech", label: "Tech (comma separated)", type: "csv" },
  { name: "order", label: "Order", type: "number" }
];

const PROJECT_FIELDS = [
  { name: "title", label: "Title", required: true },
  { name: "category", label: "Category", type: "select", options: ["fintech", "agentic", "ml", "devops"] },
  { name: "description", label: "Description", type: "textarea", required: true },
  { name: "long_description", label: "Long Description", type: "textarea" },
  { name: "tech", label: "Tech (comma separated)", type: "csv" },
  { name: "demo_url", label: "Demo URL" },
  { name: "repo_url", label: "Repo URL" },
  { name: "image_url", label: "Image URL", type: "image" },
  { name: "featured", label: "Featured?", type: "bool" },
  { name: "live", label: "Live Demo?", type: "bool" },
  { name: "order", label: "Order", type: "number" }
];

const SKILL_FIELDS = [
  { name: "category", label: "Category", required: true },
  { name: "items", label: "Items (comma separated)", type: "csv" },
  { name: "order", label: "Order", type: "number" }
];

const CERT_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "issuer", label: "Issuer" },
  { name: "year", label: "Year" },
  { name: "credential_url", label: "Credential URL" },
  { name: "order", label: "Order", type: "number" }
];

const EDU_FIELDS = [
  { name: "degree", label: "Degree", required: true },
  { name: "institution", label: "Institution", required: true },
  { name: "location", label: "Location" },
  { name: "start_year", label: "Start Year" },
  { name: "end_year", label: "End Year" },
  { name: "description", label: "Description", type: "textarea" },
  { name: "order", label: "Order", type: "number" }
];

const TESTI_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "role", label: "Role" },
  { name: "company", label: "Company" },
  { name: "quote", label: "Quote", type: "textarea", required: true },
  { name: "avatar_url", label: "Avatar URL", type: "image" },
  { name: "order", label: "Order", type: "number" }
];

const ACHIEVEMENT_FIELDS = [
  { name: "title", label: "Title", required: true },
  { name: "organization", label: "Organization" },
  { name: "year", label: "Year" },
  { name: "category", label: "Category" },
  { name: "description", label: "Description", type: "textarea" },
  { name: "url", label: "URL" },
  { name: "order", label: "Order", type: "number" }
];

const GALLERY_FIELDS = [
  { name: "src", label: "Image", type: "image", required: true, placeholder: "/library.jpg" },
  { name: "caption", label: "Caption" },
  { name: "alt", label: "Alt Text" },
  { name: "order", label: "Order", type: "number" }
];
